import { get } from "lodash-es";
import React, { FC, useEffect } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from "@mui/material";

import { ReduxTextField } from "../../../components/Redux/TextField";
import SettingSection from "../../../components/SettingSection";
import {
  getPolicyFormProperty,
  setPolicyForm,
  setPolicyFormError,
  setPolicyFormProperty,
} from "../../../slices/policies";
import { useDispatch, useSelector } from "../../../store";
import {
  EntityInfo,
  Policy,
  useCreatePolicyMutation,
  useListDefaultTemplatesQuery,
} from "../../../store/pomeriumApi";
import { notEmpty } from "../../../validators/not-empty";
import PPLBuilder from "./PPLBuilder";

type AddPolicyDialogProps = {
  open: boolean;
  onAdd: (policy: EntityInfo) => void;
  onCancel: () => void;
};
const AddPolicyDialog: FC<AddPolicyDialogProps> = ({
  open,
  onAdd,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const [addPolicy] = useCreatePolicyMutation();
  const { data: defaultPolicy, isLoading: defaultLoading } =
    useListDefaultTemplatesQuery("policy", {
      skip: !open,
    });
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const form = useSelector((state) => state.policies.form);
  const allowSave = !!form?.name;

  // reset the policy form properties
  useEffect(() => {
    if (open && !defaultLoading) {
      dispatch(
        setPolicyForm(get(defaultPolicy, "0.defaultProperties", {}) as Policy),
      );
    }
  }, [open, defaultLoading]);

  if (!open) {
    return <></>;
  }

  const handleClose = () => onCancel();
  const handleAdd = async () => {
    const policy = await addPolicy({
      organizationId,
      policyProperties: { ...form, namespaceId: clusterId } as Policy,
    }).unwrap();
    onAdd(policy);
  };

  return (
    <Dialog open={open} onClose={handleClose} scroll="body" maxWidth="md">
      <DialogTitle>Add Policy</DialogTitle>
      <DialogContent>
        <SettingSection label="Name" docsKey="policy-name">
          <ReduxTextField
            fullWidth
            path="name"
            setter={setPolicyFormProperty}
            getter={getPolicyFormProperty}
            errorHandler={setPolicyFormError}
            validator={notEmpty}
            autoFocus
          />
        </SettingSection>

        <Divider />

        <Box sx={{ pt: 2 }}>
          <PPLBuilder readonly={false} inDialog />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAdd} disabled={!allowSave}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default AddPolicyDialog;
